<template>
  <div>
    <div
      ref="header"
      class="section padding-y no-spacing-children"
    >
      <slot name="header">
        <div class="flex justify-between align-center">
          <h2 class="title title--2 layout--grow">
            {{ content.OrderSummary.title }}
          </h2>

          <p
            v-if="orderDetails.fulfillmentType === 'dine-in' && tableNumberDisplay"
            class="center"
          >
            <span class="smaller no-wrap">
              {{ userLocationType.description }}
            </span>
            <br>
            <b class="bigger dark">{{ tableNumberDisplay }}</b>
          </p>
        </div>
        <p
          v-if="content.OrderSummary.description && content.OrderSummary.description.length"
          class="quarter-spacing-top"
        >
          {{ content.OrderSummary.description }}
        </p>
      </slot>
    </div>

    <div
      v-if="!selectedLocation.storeId"
      class="section section--alt-background center-550-up"
    >
      <p class="title title--4">
        No location selected.
      </p>

      <p>
        Please select a location to start adding items to your cart.
      </p>

      <button
        class="button button--primary button--large button--auto-width"
        type="button"
        @click="$openModal('editOrderDetails', null, {
          closeModal: '*'
        })"
      >
        Select Location
      </button>
    </div>

    <div
      v-else-if="!selectedLocation.isReadyForOrdering"
      class="section section--alt-background center-550-up"
    >
      <p class="title title--4">
        <template v-if="selectedLocation.outsideGeoFence">Location is too far away to accept orders</template>
        <template v-else>Location is currently closed.</template>
      </p>

      <p>
        Browse our menu and get some ideas for later.
      </p>

      <button
        class="button button--primary button--large button--auto-width"
        type="button"
        @click="browseMenu"
      >
        Browse Menu
      </button>
    </div>

    <div
      v-else-if="menu.unavailablePerSchedule"
      class="section section--alt-background center-550-up"
    >
      <p class="title title--4">
        This menu is currently unavailable.
      </p>

      <p>Please feel free to browse our menu to get some ideas for later.</p>

      <button
        class="button button--primary button--large button--auto-width"
        type="button"
        @click="browseMenu"
      >
        Browse Menu
      </button>
    </div>

    <div
      v-else-if="order.orderItemCount === 0"
      class="section section--alt-background center-550-up"
    >
      <p class="title title--4">
        Your cart is currently empty.
      </p>

      <p>
        Browse our menu to start adding items to your cart.
      </p>

      <div class="buttons buttons--inline buttons--vertical-550-up">
        <button
          class="button button--primary button--large button--auto-width"
          type="button"
          @click="browseMenu"
        >
          Browse Menu
        </button>

        <button
          v-if="showGroupOrderButton"
          class="button button--primary button--transparent button--large button--auto-width normalcase"
          title="Start a group order"
          type="button"
          @click="$openModal('editGroupOrder')"
        >
          <span class="icon far fa-users" /> Start a Group Order
        </button>
      </div>
    </div>

    <template v-else>

      <upsell-order-items
        v-if="displayUpsellItems && merchant.features['order-summary-move-upsell-to-top']"
        class="section--half-y-padding"
      />

      <validated-form name="payment">
        <div
          v-if="shippingFullNameIsInvalid"
          class="section section--half-y-padding reverse-colors section--danger"
        >
          <p class="smaller">Full name is required for all shipping orders. Please edit your selected address.</p>
        </div>

        <template v-if="orderDetails.fulfillmentType !== 'dine-in'">
          <div
            class="section section--half-y-padding"
            :class="{
              'section--danger-light': shippingFullNameIsInvalid
            }"
          >
            <selected-location-order-details>
              <template
                v-if="orderDetails.fulfillmentType !== 'room-service'"
                #pick-up-title
              >
                <div class="layout layout--horizontal layout--stack-600">
                  <h3 class="title title--6 half-spacing grow">
                    {{ content.OrderSummary.metadata.location }}
                  </h3>
                </div>
              </template>
            </selected-location-order-details>

            <template v-if="displayEditableRoomNumber">
              <text-input
                v-model="roomNumber"
                type="text"
                label="Room Number"
                name="roomNumber"
                icon-class="fa-door-closed"
                :validations="{
                  required: true
                }"
              />
            </template>
          </div>

          <div
            v-if="orderDetails.fulfillmentType !== 'shipping'"
            ref="timeSelectWrapper"
            class="section section--half-y-padding"
          >
            <fulfillment-time-select
              ref="timeSelect"
              @valid-selection="loadUpdatedOrder"
            />
          </div>

          <div
            v-if="selectedLocation.pickupFulfillmentTypes.length > 1 && orderDetails.fulfillmentType === 'pick-up'"
            class="section section--alt-background section--half-y-padding"
          >
            <radio-input
              v-model="pickupType"
              name="pickupType"
              radio-style="box"
              label="Pickup Method"
              :options="computedPickUpTypeRadioOptions"
              :validations="{
                required: true
              }"
            />
          </div>
        </template>

        <div
          v-if="orderDetails.fulfillmentType === 'shipping' && merchant.options.shipmentGiftingEnabled"
          class="section section--half-y-padding"
        >
          <h3 class="title title--6 no-spacing">
            Add a Note
          </h3>

          <p class="half-spacing">Shipping as a gift? Add an optional note.</p>

          <text-input
            v-model="shippingGiftMessage"
            type="text"
            label="Gift Note"
            name="shippingGiftMessage"
            :show-as-optional="true"
            :validations="{
              max: 200
            }"
          />
        </div>

        <div
          v-if="orderDetails.fulfillmentType === 'shipping'"
          class="section section--half-y-padding"
        >
          <shipping-method-selector />

          <text-input
            v-model="shippingInstructions"
            type="text"
            label="Shipping Instructions"
            :placeholder="content.ShippingInstructions.metadata.copy"
            name="shippingInstructions"
          />
        </div>

        <div class="section section--half-y-padding">
          <div class="layout layout--horizontal layout--horizontal-borders layout--justify-start">
            <h3 class="title title--6">
              {{ content.OrderSummary.metadata.orderSummary }}
            </h3>

            <button
              v-if="orderDetails.orderType !== 'group'"
              class="link link--margin danger regular smaller"
              type="button"
              @click="cancelOrder"
            >
              Reset
            </button>
          </div>

          <order-items
            :hide-prices="selectedLocation.features.HidePrices"
            :hide-items="orderDetails.orderType === 'group'"
            :ignore-invalid-items="!hasFetchedOrderWithTime"
            :order="order"
            :totals="{
              tip: tip,
              totalWithTip: totalWithTip
            }"
          >

            <div
              v-if="showUtensilsToggle"
              class="section section--border-top section--bleed"
            >
              <switch-input
                v-model="includeUtensils"
                :label="content.OrderSummary.metadata.includeUtensils"
                name="requestUtensils"
              />
            </div>

            <div
              v-if="!selectedLocation.features.HidePrices"
              class="section section--bleed section--alt-background section--half-y-padding"
            >
              <tip-selection
                v-if="orderDetails.fulfillmentType !== 'shipping'"
                v-model="tip"
                class="section__child"
                :label="content.OrderSummary.metadata.tip"
                :total="totalForTipPercentage"
              />

              <apply-cardfree-offer class="section__child" />

              <apply-discount class="section__child" />
            </div>
          </order-items>

          <text-input
            v-if="merchant.options.orderInstructionsEnabled"
            v-model="orderLevelInstructions"
            type="text"
            :label="content.OrderLevelInstructions.metadata.inputPlaceholder"
            name="orderLevelInstructions"
          />

          <text-input
            v-if="orderDetails.fulfillmentType === 'delivery'"
            v-model="dropOffInstructions"
            type="text"
            label="Delivery Instructions"
            placeholder="Please provide any specific instructions here..."
            name="dropOffInstructions"
          />
        </div>

        <upsell-order-items
          v-if="displayUpsellItems && !merchant.features['order-summary-move-upsell-to-top'] "
          class="section--half-y-padding"
        />

        <div class="section section--alt-background section--shadow section--half-y-padding">
          <payment-methods
            ref="paymentMethods"
            :confirm-pickup-location="orderDetails.fulfillmentType === 'pick-up' && merchant.options.confirmPickupLocationOnCheckout"
            :disabled="isDisabled"
            :pay-cta-text="content.PaymentScreen.metadata.buttonCta"
            :hidden-elements="{
              total: selectedLocation.features.HidePrices
            }"
            :tip="tip"
            :total="totalWithTip"
            @checkout="checkout"
          />
        </div>

        <div
          v-if="!orderValidations.isValid"
          class="section section--danger section--shadow section--half-y-padding sticky-bottom reverse-colors"
        >
          <p
            v-for="error in orderValidations.errors"
            :key="error"
          >
            {{ error }}
          </p>
        </div>
      </validated-form>
    </template>



    <modal
      v-slot="{ data }"
      name="confirmTime"
      modal-style="compact"
      :is-required="true"
    >
      <div class="section section--half-y-padding center">
        <span class="icon icon--block icon--medium far fa-clock primary extra-spacing" />

        <p
          v-if="data.shouldConfirmUpdatedTime"
          class="dark half-spacing"
        >
          <template v-if="orderDetails.fulfillmentType === 'delivery'">
            Delivery
          </template>
          <template v-else>
            Pickup
          </template>
          {{ $momentCalendar(data.previouslySelectedTime, null, selectedLocation.timeZone) }} is no longer available.

          The soonest, available
          <template v-if="orderDetails.fulfillmentType === 'delivery'">
            delivery
          </template>
          <template v-else>
            pickup
          </template>
          time is:
        </p>

        <p
          v-else
          class="dark bigger no-spacing"
        >
          Your
          <template v-if="orderDetails.fulfillmentType === 'delivery'">
            delivery
          </template>
          <template v-else>
            pickup
          </template>
          time is:
        </p>

        <p class="title title--3 bold capitalize">{{ $momentCalendar(orderDetails.fulfillmentTime, null, selectedLocation.timeZone) }}</p>
      </div>

      <div class="section section--half-y-padding section--alt-background center">
        <p class="extra-spacing">Would you like to submit your order?</p>

        <div class="layout layout--horizontal layout--horizontal-equal">
          <button
            type="button"
            class="button button--light button--large"
            @click="confirmTime(false)"
          >
            No Thanks
          </button>

          <button
            type="button"
            class="button button--primary button--large"
            @click="confirmTime(true)"
          >
            Place Order
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';


  export default {
    name: 'StandardCart',

    data() {
      return {
        dropOffInstructions: null,
        hasConfirmedTime: false,
        hasFetchedOrderWithTime: false,
        includeUtensils: false,
        lastFetchedOrderWithPickupTime: null,
        orderLevelInstructions: null,
        roomNumber: null,
        shippingGiftMessage: null,
        shippingInstructions: null,
        submittedCheckoutOptions: null,
        selectedPickupType: '',
        tip: 0
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'orderItemsUnavailablePerSchedule', 'tableNumber', 'showGroupOrderButton', 'tableNumberDisplay', 'userLocationType', 'orderValidations', 'currentCapiOrderType']),
      ...mapGetters('session', ['orderDetails']),
      ...mapGetters('shipping', ['selectedShippingMethod']),

      displayEditableRoomNumber() {
        /** A user can edit their table number if the url does not have an imbedded table number (ie. tb=100) */
        return this.orderDetails.fulfillmentType === 'room-service' && !this.tableNumberDisplay;
      },

      isDisabled() {
        if (!this.orderValidations.isValid || this.shippingFullNameIsInvalid) {
          return true;
        }

        if (this.totalWithTip === 0 && this.currentCapiOrderType !== 'TipOnly') {
          if (this.order.discount || this.selectedLocation.allowNoPaymentInfo) {
            return false;
          }

          return true;
        }

        return false;
      },

      shippingFullNameIsInvalid() {
        return this.orderDetails.fulfillmentType === 'shipping' && !this.orderDetails.deliveryAddress.addressee;
      },

      totalForTipPercentage() {
        let total = this.order.orderItemTotal;

        if (this.merchant.options.calculateTipAfterTax) {
          total += this.order.tax;
        }

        return total;
      },

      totalWithTip() {
        const total = this.tip + this.order.total;

        return Math.round(total * 100) / 100;
      },

      pickupType: {
        get() {
          return this.selectedPickupType;
        },

        set(updatedValue) {
          this.selectedPickupType = updatedValue;

          this.$store.dispatch('session/updateOrderDetails', {
            ...this.orderDetails,
            pickupType: updatedValue
          });
        }
      },

      showUtensilsToggle() {
        return this.selectedLocation.features.IncludeUtensilsEnabled
          && (this.orderDetails.fulfillmentType === 'delivery' || this.orderDetails.fulfillmentType === 'pick-up');
      },

      computedPickUpTypeRadioOptions() {
        const content = this.content.OrderSummary?.metadata;

        const result = this.selectedLocation.pickupFulfillmentTypes.map((type) => {
          if (type.value === 'curbside') return { ...type, subDisplay: content.curbsideSelection, icon: 'fa-car' };
          if (type.value === 'take-out') return { ...type, subDisplay: content.insideSelection, icon: 'fa-store' };
          if (type.value === 'drive-through') return { ...type, subDisplay: content.driveThroughSelection, icon: 'fa-car-building' };

          return type;
        });

        return result;
      },

      hasNotMetMetUpsellModalTrigger() {
        let cartItemTotal = 0;

        this.order.orderItems.forEach((orderItem) => {
          if (orderItem.units) {
            cartItemTotal += orderItem.quantity * orderItem.units;
          }
          else {
            cartItemTotal += orderItem.quantity;
          }
        });

        const upsellItem = this.order.upsellRules?.find(upsellRule => !!upsellRule.triggerAmount);

        return this.order.upsellDisplayMode === 'Modal' && cartItemTotal < upsellItem?.triggerAmount;
      },

      displayUpsellItems() {
        return this.orderDetails.orderType !== 'group' && (this.order.upsellDisplayMode === 'Checkout' || this.hasNotMetMetUpsellModalTrigger);
      }
    },

    created() {
      this.createdHandler();

      this.selectedPickupType = this.selectedLocation.features.RequirePickupMethodSelection
        ? ''
        : this.orderDetails.pickupType;
    },

    methods: {
      browseMenu() {
        this.$closeModal('cart');

        this.$router.push({
          name: 'orderMenu',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      },

      cancelOrder() {
        this.$openModal('confirmation', {
          title: 'Reset Order',
          primaryText: 'Warning! You are about to reset your order and remove everything from your cart.',
          ctaConfirmText: 'Confirm',
          onConfirm: () => this.$store.dispatch('orders/cancelOrder'),
          onConfirmSuccess: () => {
            this.$closeAllModals();
          }
        });
      },

      async checkout(options) {
        this.submittedCheckoutOptions = options;
        let pickupTime;

        if (this.$refs.timeSelect && !this.hasConfirmedTime) {
          const result = await this.$refs.timeSelect.verifyTime();
          pickupTime = result.pickupTime;

          if (result.shouldConfirmUpdatedTime || result.shouldConfirmAsapTime) {
            this.$openModal('confirmTime', result);
            return;
          }
        }

        else {
          pickupTime = await this.$store.dispatch('session/returnActualPickupTime');
        }

        this.checkoutSubmit(options, pickupTime);
      },

      async checkoutSubmit(options, pickupTime) {
        try {
          const body = {
            contactInfo: options.contactInfo,
            orderLevelInstructions: this.orderLevelInstructions,
            paymentMethod: options.paymentMethod,
            pickupDate: pickupTime
          };

          if (this.orderDetails.fulfillmentType === 'delivery') {
            body.deliveryRequest = {
              deliveryService: this.orderDetails.deliveryEstimate.deliveryService,
              deliveryTime: this.orderDetails.deliveryEstimate.deliveryTime,
              tip: this.tip,
              dropOffInstructions: this.dropOffInstructions,
              dropOffAddress: {
                city: this.orderDetails.deliveryAddress.city,
                countryCode: this.orderDetails.deliveryAddress.countryCode,
                line1: this.orderDetails.deliveryAddress.line1,
                line2: this.orderDetails.deliveryAddress.line2,
                postalCode: this.orderDetails.deliveryAddress.postalCode,
                state: this.orderDetails.deliveryAddress.state
              }
            };
            body.includeUtensils = this.includeUtensils;
            body.pickupLocation = 'take-out';
          }

          else if (this.orderDetails.fulfillmentType === 'dine-in') {
            body.tip = this.tip;
            body.pickupLocation = 'dine-in';
            body.sublocationId = this.tableNumber;
          }

          else if (this.orderDetails.fulfillmentType === 'pick-up') {
            body.includeUtensils = this.includeUtensils;
            body.tip = this.tip;
            body.pickupLocation = this.pickupType;
          }

          else if (this.orderDetails.fulfillmentType === 'room-service') {
            body.tip = this.tip;
            body.pickupLocation = 'room-service';
            body.sublocationId = this.roomNumber;
          }

          else if (this.orderDetails.fulfillmentType === 'shipping') {
            body.shipmentRequest = {
              shippingGiftMessage: this.shippingGiftMessage,
              shippingInstructions: this.shippingInstructions,
              shippingMethod: this.selectedShippingMethod.shippingMethod,
              shippingProvider: this.selectedShippingMethod.shippingProvider,
              shipToAddress: {
                addressee: this.orderDetails.deliveryAddress.addressee,
                city: this.orderDetails.deliveryAddress.city,
                countryCode: this.orderDetails.deliveryAddress.countryCode,
                line1: this.orderDetails.deliveryAddress.line1,
                line2: this.orderDetails.deliveryAddress.line2,
                postalCode: this.orderDetails.deliveryAddress.postalCode,
                state: this.orderDetails.deliveryAddress.state
              }
            };
            body.pickupLocation = 'shipping';
            delete body.pickupDate;
          }

          const payment = await this.$store.dispatch('orders/submitOrder', body);

          // this.$triggerGoogleTrackingEvent('PlaceOrderSuccess', 'Checkout');

          this.$triggerNewRelicEvent('PlaceOrderSuccess', 'Information', 'Place Order Success');

          options.onCheckoutSuccess();

          this.$router.push({
            name: 'orderReceipt',
            params: {
              merchantId: this.$route.params.merchantId,
              receiptToken: payment.receiptToken
            }
          });

          this.$closeModal('cart');
        }

        catch (error) {
          // this.$triggerGoogleTrackingEvent('PlaceOrderFailure', 'Checkout');

          this.$triggerNewRelicEvent('PlaceOrderFailure', 'Error', 'Place Order Failure');

          await this.$store.dispatch('orders/handleSubmitError', {
            error,
            pickupTime
          });

          options.onCheckoutError();

          if (this.$refs.timeSelect) {
            this.$refs.timeSelect.initializePickUpTimeSelection();
          }

          if (this.$refs.header) {
            this.$refs.header.scrollIntoView();
          }
        }
      },

      confirmTime(isConfirmed) {
        this.hasConfirmedTime = isConfirmed;

        this.$closeModal('confirmTime');

        if (isConfirmed) {
          this.checkout(this.submittedCheckoutOptions);
        }

        else {
          this.submittedCheckoutOptions.onCheckoutError();
          this.invalidTimeSelectionHandler();
        }
      },

      createdHandler() {
        this.$store.dispatch('locations/getLocation', { silent: true, withMenu: true });

        if (this.orderDetails.fulfillmentType === 'room-service') {
          this.roomNumber = this.tableNumber;
        }

        this.triggerEventTracking();
      },

      invalidTimeSelectionHandler() {
        this.$refs.paymentMethods.close();
        this.$refs.timeSelectWrapper.scrollIntoView();
      },

      async loadUpdatedOrder() {
        const pickupTime = await this.$store.dispatch('session/returnActualPickupTime');

        if (pickupTime && this.lastFetchedOrderWithPickupTime !== pickupTime) {
          this.lastFetchedOrderWithPickupTime = pickupTime;

          await this.$store.dispatch('orders/getOrder', { pickupTime, silent: true });

          this.hasFetchedOrderWithTime = true;
        }
      },

      triggerEventTracking() {
        const { orderItems, tax, subtotal } = this.order;

        const eventData = { itemInfo: orderItems, paymentInfo: { total: subtotal, tax, tip: this.tip } };

        this.$googleEventTracking.postEvent('view_cart', eventData);
      }
    }
  };
</script>
